import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PaginationState } from "@tanstack/react-table";

import Loader from "../../../shared/Ui/Loader/Loader";
import { EmptyListPlaceholderContent } from "../../../shared/Ui/EmptyPageBlock";
import StatusBadge from "../../../shared/Ui/StatusBadge/StatusBadge";
import { SelectCustom } from "../../../shared/Ui/SelectCustom/SelectCustom";
import PageNotRole from "../../../shared/Ui/PageNotFound/PageNotRole";
import Pagination from "../../../shared/components/Table/Pagination";
import { useAppSelector } from "../../../shared/hook";
import {
  ParticipantsRegistryReportItem,
  ParticipantsRegistryReportPreviewResponse,
  ParticipantsRegistryReportRow,
  ParticipantsRegistrySort,
  downloadParticipantsRegistryReport,
  getParticipantsRegistryReport,
  previewParticipantsRegistryReport,
} from "../../../common/api/participantsRegistryReportApi";
import { downloadXlsxBlob } from "../../Billing/billingUtils";
import {
  canAccessParticipantsRegistry,
  formatDisplayDateTime,
  getReportDate,
  getReportStatusMeta,
} from "./participantsRegistryUtils";

const P = "participants_registry_report";
const COL = `${P}.columns`;

// Порядок и названия — как в выходной форме отчёта
const PREVIEW_COLUMNS: { key: keyof ParticipantsRegistryReportRow; labelKey: string }[] = [
  { key: "iinBin", labelKey: `${COL}.iin_bin` },
  { key: "organizationName", labelKey: `${COL}.organization_name` },
  { key: "position", labelKey: `${COL}.position` },
  { key: "fullName", labelKey: `${COL}.full_name` },
  { key: "email", labelKey: `${COL}.email` },
  { key: "phone", labelKey: `${COL}.phone` },
  { key: "region", labelKey: `${COL}.region` },
  { key: "legalAddress", labelKey: `${COL}.legal_address` },
  { key: "productGroup", labelKey: `${COL}.product_group` },
  { key: "registrationDate", labelKey: `${COL}.registration_date` },
  { key: "contractNumber", labelKey: `${COL}.contract_number` },
  { key: "contractDate", labelKey: `${COL}.contract_date` },
  { key: "iban", labelKey: `${COL}.iban` },
  { key: "bankName", labelKey: `${COL}.bank_name` },
  { key: "dcbAccount", labelKey: `${COL}.dcb_account` },
  { key: "globalAccount", labelKey: `${COL}.global_account` },
  { key: "participantRoles", labelKey: `${COL}.participant_roles` },
];

const SORT_OPTIONS: { value: ParticipantsRegistrySort; labelKey: string }[] = [
  { value: "REGISTRATION_DATE", labelKey: `${P}.sort_registration_date` },
  { value: "ORGANIZATION_NAME", labelKey: `${P}.sort_organization_name` },
  { value: "CONTRACT_DATE", labelKey: `${P}.sort_contract_date` },
];

const PREVIEW_MAX_PAGE_SIZE = 100;

function ParticipantsRegistryReportDetailsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reportId } = useParams();
  const userData: any = useAppSelector((state) => state.auth.userData);
  const hasAccess = canAccessParticipantsRegistry(userData);

  const [report, setReport] = useState<ParticipantsRegistryReportItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const pageSize = useAppSelector((state) => state.userSettings.pageSize);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [sort, setSort] = useState<ParticipantsRegistrySort | null>(null);
  const [preview, setPreview] =
    useState<ParticipantsRegistryReportPreviewResponse | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isPreviewError, setIsPreviewError] = useState(false);

  const loadReport = async (id: number) => {
    try {
      const data = await getParticipantsRegistryReport(id);
      setReport(data);
    } catch (error) {
      console.error("Error loading participants registry report:", error);
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAccess || !reportId || !/^\d+$/.test(reportId)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    loadReport(Number(reportId));
  }, [reportId, hasAccess]);

  useEffect(() => {
    if (!reportId || report?.status !== "FORMING") return;
    const timer = window.setInterval(() => loadReport(Number(reportId)), 2500);
    return () => window.clearInterval(timer);
  }, [reportId, report?.status]);

  const hasRows = report?.status === "FORMED" && Boolean(report.rowsCount);
  // Сортировка предпросмотра: по умолчанию — та, с которой отчёт формировался
  const activeSort: ParticipantsRegistrySort =
    sort ?? report?.filters?.sort ?? "REGISTRATION_DATE";

  useEffect(() => {
    if (!report || !hasRows) return;
    setIsPreviewLoading(true);
    setIsPreviewError(false);
    previewParticipantsRegistryReport(
      { ...report.filters, sort: activeSort },
      pagination.pageIndex + 1,
      Math.min(pagination.pageSize, PREVIEW_MAX_PAGE_SIZE),
    )
      .then(setPreview)
      .catch((error) => {
        console.error("Error loading participants registry preview:", error);
        setPreview(null);
        setIsPreviewError(true);
      })
      .finally(() => setIsPreviewLoading(false));
  }, [report?.id, hasRows, activeSort, pagination.pageIndex, pagination.pageSize]);

  const handleDownload = async () => {
    if (!report?.id) return;
    setIsExporting(true);
    try {
      const blob = await downloadParticipantsRegistryReport(report.id);
      const fileName =
        report.files?.find((file) => file.format === "XLSX")?.fileName ||
        `${report.reportNumber || `participants-registry-${report.id}`}.xlsx`;
      downloadXlsxBlob(blob, fileName);
    } catch (error) {
      console.error("Error downloading participants registry report:", error);
      window.alert(t("billing.exportError"));
    } finally {
      setIsExporting(false);
    }
  };

  if (!hasAccess) return <PageNotRole />;

  if (isLoading) return <Loader />;

  if (!report) {
    return (
      <div className="flex flex-1 flex-col min-w-0 p-4">
        <EmptyListPlaceholderContent title={t("marking_codes_report.load_error")} />
        <button
          type="button"
          className="mt-4 self-start bg-bg text-gray-600 font-bold py-2 px-4 rounded"
          onClick={() => navigate("/reports/participants-registry")}
        >
          {t("billing.close")}
        </button>
      </div>
    );
  }

  const st = getReportStatusMeta(report.status, t);
  const canDownload = report.status === "FORMED" && Boolean(report.rowsCount);
  const previewRows = preview?.rows ?? [];
  // Пока ответа нет (включая первый рендер до старта запроса) — показываем лоадер вместо таблицы
  const isPreviewPending = isPreviewLoading || (!preview && !isPreviewError);

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <h2 className="my-3 text-xl font-semibold">{t(`${P}.title`)}</h2>

      {report.status === "FORMING" && (
        <div className="bg-white rounded-xl shadow-md p-8 mb-5 flex flex-col items-center justify-center gap-3">
          <div className="w-10 h-10 border-4 border-green/30 border-t-green rounded-full animate-spin" />
          <p className="text-sm text-gray-600">
            {t("marking_codes_report.status_forming")}...
          </p>
        </div>
      )}

      {report.status === "ERROR" && (
        <div className="bg-white rounded-xl shadow-md p-5 mb-5">
          <EmptyListPlaceholderContent
            title={t("marking_codes_report.status_error")}
          />
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md p-5">
        <div className="flex flex-row items-start justify-between gap-4 mb-4 flex-wrap">
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              <span className="font-semibold">
                {t("marking_codes_report.report_number")}:
              </span>{" "}
              {report.reportNumber || `№ ${report.id}`}
            </p>
            <p>
              <span className="font-semibold">{t("marking_codes_report.status")}:</span>{" "}
              <span className="inline-flex align-middle ml-1">
                <StatusBadge type={st.type} fontSize="14px">
                  {st.label}
                </StatusBadge>
              </span>
            </p>
            <p>
              <span className="font-semibold">
                {t("marking_codes_report.generated_at")}:
              </span>{" "}
              {formatDisplayDateTime(getReportDate(report))}
            </p>
            {report.requestedByName && (
              <p>
                <span className="font-semibold">
                  {t("marking_codes_report.executor")}:
                </span>{" "}
                {report.requestedByName}
              </p>
            )}
            {report.rowsCount != null && (
              <p>
                <span className="font-semibold">
                  {t("marking_codes_report.rows_count")}:
                </span>{" "}
                {report.rowsCount}
              </p>
            )}
          </div>

          <div className="flex flex-row gap-2 items-start">
            <button
              type="button"
              className="bg-green text-white font-semibold py-2 px-4 rounded disabled:opacity-60"
              disabled={!canDownload || isExporting}
              onClick={handleDownload}
            >
              {t("mainButtons.download")}
            </button>
            <button
              type="button"
              className="bg-bg text-gray-500 font-semibold py-2 px-4 rounded"
              onClick={() => navigate("/reports/participants-registry")}
            >
              {t("billing.close")}
            </button>
          </div>
        </div>

        {report.status === "FORMED" && !report.rowsCount && (
          <EmptyListPlaceholderContent title={t(`${P}.no_data_found`)} />
        )}

        {hasRows && (
          <div className="flex flex-row items-center justify-end gap-2 mb-3">
            <span className="text-sm text-gray-600">{t(`${P}.sort`)}:</span>
            <div className="w-[300px]">
              <SelectCustom
                size="dm"
                isClearable={false}
                isSearchable={false}
                options={SORT_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
                value={{
                  value: activeSort,
                  label: t(SORT_OPTIONS.find((o) => o.value === activeSort)?.labelKey ?? ""),
                }}
                onChange={(option: any) => {
                  if (!option?.value) return;
                  setSort(option.value);
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
              />
            </div>
          </div>
        )}

        {hasRows && isPreviewPending && (
          <div className="flex flex-col items-center justify-center gap-3 py-10">
            <div className="w-10 h-10 border-4 border-green/30 border-t-green rounded-full animate-spin" />
            <p className="text-sm text-gray-600">{t("billing.loading")}</p>
          </div>
        )}

        {hasRows && !isPreviewPending && previewRows.length === 0 && (
          <EmptyListPlaceholderContent
            title={
              isPreviewError
                ? t("marking_codes_report.preview_error")
                : t(`${P}.no_data_found`)
            }
          />
        )}

        {hasRows && !isPreviewPending && previewRows.length > 0 && (
          <>
            <div className="rounded-lg overflow-x-auto">
              <table className="max-w-full w-full leading-normal">
                <thead className="border-b-2 border-gray-300">
                  <tr>
                    <th className="px-5 py-3 text-center text-gray-600 font-semibold bg-white border-b border-gray-200">
                      №
                    </th>
                    {PREVIEW_COLUMNS.map((column) => (
                      <th
                        key={column.key}
                        className="px-5 py-3 min-w-[140px] text-center text-gray-600 font-semibold bg-white border-b border-gray-200"
                      >
                        {t(column.labelKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-normal text-center">
                  {previewRows.map((row, index) => (
                    <tr key={`pr-row-${row.rowNumber ?? index}-${row.iinBin ?? ""}`}>
                      <td className="px-5 py-4 text-sm border-b border-gray-200 text-gray-600">
                        {row.rowNumber ??
                          pagination.pageIndex * pagination.pageSize + index + 1}
                      </td>
                      {PREVIEW_COLUMNS.map((column) => (
                        <td
                          key={column.key}
                          className="px-5 py-4 text-sm border-b border-gray-200 text-gray-600"
                        >
                          {row[column.key] || "-"}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              totalItems={preview?.totalItems || 0}
              totalPages={preview?.totalPages || 0}
              pagination={pagination}
              setPagination={setPagination}
            />
          </>
        )}
      </div>
    </div>
  );
}

export default ParticipantsRegistryReportDetailsPage;
